import React, { useEffect, useState, useRef } from 'react'
import messages from './messages'
import { useIntl, FormattedMessage } from 'react-intl'
import { environment, adyenOriginKey } from 'js/config'
import useLocale from '../../hooks/useLocale'
import AdyenCheckout from '@adyen/adyen-web'
import './adyen.scss'

const adyenLocales = {
  en: 'en_US',
  nl: 'nl_NL',
  sv: 'sv_SE',
  fr: 'fr_FR',
  de: 'de_DE',
  no: 'no_NO',
  ru: 'ru_RU',
  da: 'da_DK',
  it: 'it_IT',
  es: 'es_ES',
  pt: 'pt_BR',
}

export const paymentMethods = {
  card: {
    name: 'Bank card',
    type: 'scheme',
  },
  payPal: {
    configuration: {
      intent: 'capture',
    },
    name: 'PayPal',
    type: 'paypal',
  },
}

const getPaymentMethods = (currency) => {
  return currency === 'INR'
    ? [paymentMethods.card]
    : [paymentMethods.card, paymentMethods.payPal]
}

type Props = {
  children: (props: {
    formProps: {
      ref: React.RefObject<HTMLFormElement>
      className: string
    }
    payPalSelected: boolean
    adyenCheckout: React.ReactNode
  }) => React.ReactNode
  onSubmit: (state: any, dropin: any) => void
  onBeforeSubmit?: () => Promise<boolean>
  giftcard?: boolean
  emailValid: boolean
  scenario?: 'update-card'
}

function CreditCardForm({ ...props }: Props) {
  const [error, setError] = useState(false)
  const [payPalSelected, setPayPalSelected] = useState(false)
  const form = useRef<HTMLFormElement>(null!)
  const { currency, language } = useLocale()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const intl = useIntl()
  const configuration = {
    locale: adyenLocales[language],
    clientKey: adyenOriginKey,
    environment: environment === 'production' ? 'live' : 'test',
    showPayButton: false,
    paymentMethodsConfiguration: {
      paymentMethods: getPaymentMethods(currency),
    },
    paymentMethodsResponse: {
      paymentMethods: getPaymentMethods(currency),
    },
    analytics: {
      enabled: false, // do not send analytics data to Adyen.
    },
  }

  const loadAdyen = async () => {
    setError(false)
    const checkout = await AdyenCheckout(configuration)
    const dropin = checkout.create('dropin', {
      // paymentMethodsConfiguration: {
      //   card: {
      //     hasHolderName: false,
      //     enableStoreDetails: false,
      //     name: intl.formatMessage(messages.creditCard),
      //   },
      // },
      onSubmit: (state, dropin) => {
        const { onBeforeSubmit, onSubmit } = props
        const beforeSubmit = onBeforeSubmit
          ? onBeforeSubmit()
          : Promise.resolve(true)

        beforeSubmit.then((ok) => {
          if (ok) onSubmit(state, dropin)
        })
      },
      onSelect: (component) => {
        setPayPalSelected(component.props.type === 'paypal')
      },
    })
    dropin.mount('#adyen-credit-card-form')

    form.current.addEventListener('submit', (e) => {
      e.preventDefault()
      if (dropin.data?.['paymentMethod']?.type === paymentMethods.payPal.type) {
        props.onSubmit(
          { data: { paymentMethod: { type: paymentMethods.payPal.type } } },
          dropin,
        )
      } else {
        dropin.submit()
      }
    })
  }

  useEffect(() => {
    loadAdyen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <div className="mb-24 rounded-8 border-2 border-secondary-red px-24 py-32">
        <h2 className="mb-12 font-demibold text-secondary-red">
          <FormattedMessage {...messages.pay_load_error_heading} />
        </h2>
        <p>
          <FormattedMessage {...messages.pay_load_error_text} />
        </p>
        <button
          className="mt-24 rounded-full bg-white px-24 py-4 text-secondary-blue type-14-demibold-caps"
          type="button"
          onClick={() => {
            loadAdyen()
          }}
        >
          <FormattedMessage {...messages.pay_try_again} />
        </button>
      </div>
    )
  }

  return props.children({
    formProps: {
      ref: form,
      className: 'c-pay__form',
    },
    payPalSelected: payPalSelected,
    adyenCheckout: (
      <div className="c-pay__fields-wrapper">
        <div id="adyen-credit-card-form" className="adyen-checkout" />
      </div>
    ),
  })
}

export default CreditCardForm

export const AcceptedPaymentMethods = () => (
  <picture>
    <source
      srcSet={`${require('media/payment-methods.png')}, ${require('media/payment-methods@2x.png')} 2x`}
    />
    <img
      className="mx-auto mt-24 block"
      src={require('media/payment-methods.png')}
      alt="We accept Visa, MasterCard, American Express and PayPal"
      height="21"
      width="180"
    />
  </picture>
)

export const SecurePayments = () => (
  <p className="c-pay__secure mb-12">
    <FormattedMessage {...messages.securePayments} />
  </p>
)
